import React from 'react';
import type { ContentBlock } from '../../types';

interface EpiphanyBridgeContentProps {
  content: ContentBlock[];
}

const EpiphanyBridgeContent: React.FC<EpiphanyBridgeContentProps> = ({ content }) => {
  // DEFINITIVE FIX: Add a guard to prevent the component from crashing if the content prop is not a valid array.
  if (!content || !Array.isArray(content)) {
    return null;
  }
  
  const generateImageProps = (url: string) => {
    if (!url || !url.includes('pexels.com')) {
      return { src: url };
    }
    try {
        const baseUrl = url.split('?')[0];
        const baseParams = "auto=compress&cs=tinysrgb&fm=webp&q=75";
        const widths = [400, 800];
        const srcSet = widths.map(w => `${baseUrl}?${baseParams}&w=${w} ${w}w`).join(', ');
        return {
          src: `${baseUrl}?${baseParams}&w=800`,
          srcSet,
        };
    } catch (e) {
        return { src: url };
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-6">
      {content.map((block, index) => {
        // Add a key guard to prevent rendering malformed blocks
        if (!block || !block.type) return null;

        switch (block.type) {
          case 'heading':
            return (
              <h2 key={index} className="text-3xl md:text-4xl font-serif font-bold mt-12 mb-4 first:mt-0">
                {block.content}
              </h2>
            );
          case 'paragraph':
            return (
              <p key={index} className="text-lg text-white/90 leading-relaxed my-4">
                {block.content}
              </p>
            );
          case 'image':
            const imageProps = generateImageProps(block.imageUrl || '');
            return (
              <figure key={index} className="my-12">
                <img 
                  {...imageProps}
                  sizes="(max-width: 768px) 100vw, 800px"
                  alt={block.caption || ''}
                  className="w-full aspect-video object-cover rounded-2xl shadow-lg border border-white/10"
                  loading="lazy"
                  width="800"
                  height="450"
                />
                {block.caption && (
                  <figcaption className="mt-3 text-center text-sm text-white/70 italic">
                    {block.caption}
                  </figcaption>
                )}
              </figure>
            );
          case 'callout':
            return (
              <div 
                key={index} 
                className="my-10 p-6 rounded-2xl border-l-4 border-brand-gold bg-yellow-400/10 shadow-lg"
              >
                <p className="text-xl font-semibold italic text-yellow-300/90">
                  "{block.content}"
                </p>
              </div>
            );
          default:
            return null;
        }
      })}
    </div>
  );
};

export default EpiphanyBridgeContent;